import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, ChevronRight, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const NAV_LINKS = [
  { to: '/rolunk', label: 'Rólunk' },
  { to: '/szolgaltatasok', label: 'Szolgáltatások' },
  { to: '/hirek', label: 'Hírek' },
  { to: '/kapcsolat', label: 'Kapcsolat' },
];

export function Header() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="group flex items-center gap-0 px-2.5 py-1.5 -ml-2.5 rounded-xl border border-transparent hover:border-[#0B1B3D]/10 hover:bg-[#0B1B3D]/[0.03] transition-all duration-200">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#0B1B3D] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm sm:text-[13px] font-['Outfit']">AGM</span>
            </div>
            <span className="hidden sm:block ml-2 text-[#0B1B3D] font-semibold text-[17px] font-['Outfit'] tracking-tight drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]">
              Consulting
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${to.slice(1)}-link`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  location.pathname === to
                    ? 'text-[#0B1B3D] bg-[#0B1B3D]/5'
                    : 'text-[#64748B] hover:text-[#0B1B3D] hover:bg-[#0B1B3D]/5'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {user && user.role === 'admin' ? (
              <Link to="/admin">
                <Button variant="outline" size="sm" data-testid="admin-panel-link"
                  className="border-[#0B1B3D] text-[#0B1B3D] hover:bg-[#0B1B3D] hover:text-white">
                  Admin
                </Button>
              </Link>
            ) : null}
            <Link to={user ? '/portal' : '/bejelentkezes'}>
              <Button variant="outline" size="sm" data-testid="portal-link"
                className="border-[#0B1B3D] text-[#0B1B3D] hover:bg-[#0B1B3D] hover:text-white">
                {user ? 'Ügyfélportál' : 'Bejelentkezés'}
              </Button>
            </Link>
            <Link to="/kapcsolat">
              <Button size="sm" data-testid="cta-header-btn"
                className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                Ajánlatkérés
              </Button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[#0B1B3D]"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#E2E8F0] animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === to
                    ? 'text-[#0B1B3D] bg-[#0B1B3D]/5'
                    : 'text-[#64748B] hover:text-[#0B1B3D]'
                }`}
              >
                {label}
              </Link>
            ))}
            <div className="pt-3 border-t border-[#E2E8F0] space-y-2">
              {user && user.role === 'admin' && (
                <Link to="/admin" className="block">
                  <Button variant="outline" className="w-full border-[#0B1B3D] text-[#0B1B3D]">Admin</Button>
                </Link>
              )}
              <Link to={user ? '/portal' : '/bejelentkezes'} className="block">
                <Button variant="outline" className="w-full border-[#0B1B3D] text-[#0B1B3D]">
                  {user ? 'Ügyfélportál' : 'Bejelentkezés'}
                </Button>
              </Link>
              <Link to="/kapcsolat" className="block">
                <Button className="w-full bg-[#0B1B3D] hover:bg-[#162B56] text-white">Ajánlatkérés</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-[#0B1B3D] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#C5A059] rounded-lg flex items-center justify-center">
                <span className="text-[#0B1B3D] font-bold text-base font-['Outfit']">AGM</span>
              </div>
              <span className="text-white font-semibold text-lg font-['Outfit']">Consulting</span>
            </div>
            <p className="text-[#94A3B8] text-sm leading-relaxed">
              Professzionális könyvelő és adótanácsadó szolgáltatások. Digitális működés, átláthatóság, megbízhatóság.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A059] mb-4">Navigáció</h4>
            <ul className="space-y-2">
              {[
                { to: '/rolunk', label: 'Rólunk' },
                { to: '/szolgaltatasok', label: 'Szolgáltatások' },
                { to: '/hirek', label: 'Hírek' },
                { to: '/kapcsolat', label: 'Kapcsolat' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-[#94A3B8] hover:text-white text-sm transition-colors flex items-center gap-1">
                    <ChevronRight size={14} /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A059] mb-4">Jogi</h4>
            <ul className="space-y-2">
              {[
                { to: '/jogi/adatvedelmi-tajekoztato', label: 'Adatvédelmi tájékoztató' },
                { to: '/jogi/aszf', label: 'ÁSZF' },
                { to: '/jogi/impresszum', label: 'Impresszum' },
                { to: '/jogi/visszaeles-bejelentes', label: 'Visszaélés bejelentés' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-[#94A3B8] hover:text-white text-sm transition-colors flex items-center gap-1">
                    <ChevronRight size={14} /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A059] mb-4">Kapcsolat</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-[#94A3B8] text-sm">
                <Phone size={16} className="text-[#C5A059]" /> +36 30 990 6766
              </li>
              <li className="flex items-center gap-2 text-[#94A3B8] text-sm">
                <Mail size={16} className="text-[#C5A059]" /> office@agmconsulting.hu
              </li>
              <li className="flex items-start gap-2 text-[#94A3B8] text-sm">
                <MapPin size={16} className="text-[#C5A059] mt-0.5" /> Budapest, Bródy Sándor utca 19. 1088
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-[#64748B] text-xs">
            &copy; {new Date().getFullYear()} AGM Consulting. Minden jog fenntartva.
          </p>
          <p className="text-[#78849B] text-xs font-medium mt-3 tracking-wide">
            Design & fejlesztés: <a href="https://www.boldigital.hu/" data-testid="boldigital-credit-link" className="text-[#94A3B8] hover:text-[#C5A059] transition-colors font-semibold">Boldigital</a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-16 sm:pt-20">{children}</main>
      <Footer />
    </div>
  );
}
